/**
 * src/modules/grupo.js
 *
 * Módulo de la pantalla principal del grupo (home del grupo).
 * Extraído de js/app.js en sub-fase 3.14.
 *
 * Patrón canónico C1 (paridad con 3.7-3.13).
 *
 * Estructura de renderGrupo (F1.3.b):
 *   - Soy miembro (mi fila de `jugadores` está en el cache) → dashboard.
 *   - No soy miembro / el grupo no existe → redirige a /ingreso (unirse va
 *     por el RPC unirse_a_grupo). Las ramas !currentUser y el form de join
 *     inline murieron en F1.3.b (DT-I/DT-X).
 *
 * Globals consumidos vía window.* (DT-10 — limpieza en sub-fase 3.16):
 *   - appRoot.innerHTML (4 ocurrencias) — bridge DOM root.
 *     Productor post-3.15: src/main.js.
 *   - formatNumber (4 invocaciones en 3 líneas — línea original 398
 *     contiene 2 calls en ternario) — decisión 31, inline cada vez sin
 *     alias local. Productor post-3.15: src/main.js.
 *
 * FASE 4 · F1.2: mock-login eliminado. src/modules/mock-login.js fue
 *   archivado en /legacy/ y simularIngresoExistente ya no existe. La lista
 *   "logueate como jugador existente" perdió su handler de click: con auth
 *   real (gate en src/main.js) no hay usuarios sin sesión, así que ese bloque
 *   quedó inalcanzable. Su markup se limpia junto a las ramas !currentUser
 *   en F1.3.
 *
 * Subgrafo guardian (watchHomeRituals + patchProfileRoute en index.html):
 *   NO se toca en esta sub-fase. El guardian observa el DOM resultante,
 *   no invoca renderGrupo. Bridge se elimina en 3.15.
 *
 * Autollamada interna `renderGrupo(codigo)` (handler post-join en ESTADO B):
 *   referencia lexical dentro del closure del propio `const`, NO se prefija
 *   con `window.`. Sigue funcionando en ES Module.
 */

import { getNormalizedDB } from '../core/db.js';
import { navigate } from '../core/router.js';
import { setActiveGroupCode, getMembership } from '../core/groups.js';
import { signOut } from '../core/auth.js';
import { getPactoByGroup } from '../core/pacto.js';
import { subscribirCambios, limpiarSuscripciones } from '../core/realtime.js';
import { renderActiveGroupBar, renderBottomNav } from '../ui/components.js';
import { loadHomeMatches, resultadoCentroHome } from './fixture.js';
import { lineasEstadoVivo } from '../utils/estado-partido.js';
import { withTimeout, NET_TIMEOUT_MS } from '../utils/timeout.js';
import { upsertPrediccion } from '../core/predicciones.js';
import {
    PLAYOFF_CARD_CSS,
    renderPlayoffCardHome,
    wirePlayoffCard,
    decodePrediccionPlayoff,
} from '../ui/playoff-prediction-card.js';
import { appRoot } from '../utils/dom.js';
import { formatNumber } from '../utils/format.js';
import { buildComoSeJuegaButton, maybeAutoOpenComoSeJuega } from '../ui/como-se-juega-modal.js';

// H4 migración: inyectar CSS del componente compartido una sola vez en el
// <head> del documento. Idempotente — re-imports del módulo no duplican.
if (typeof document !== 'undefined') {
    const STYLE_ID = 'ppc-shared-css';
    if (!document.getElementById(STYLE_ID)) {
        const styleEl = document.createElement('style');
        styleEl.id = STYLE_ID;
        styleEl.textContent = PLAYOFF_CARD_CSS;
        document.head.appendChild(styleEl);
    }
}

export const renderGrupo = (codigo) => {
    const db = getNormalizedDB();
    const grupo = db.grupos[codigo];
    
    if(!grupo) {
        navigate('/ingreso');
        return;
    }
    setActiveGroupCode(codigo);
    
    // ESTADO A — soy miembro: mi fila de `jugadores` ya está en el cache.
    const jugador = getMembership(codigo);
    if (jugador) {
        // Bridge transitorio: archivos no migrados (rituales/ranking/perfil)
        // leen sesion_${codigo} hasta F1.3.c/d.
        localStorage.setItem(`sesion_${codigo}`, jugador.user_id);
        // H1: player_id para persistir predicciones desde el carrusel del home.
        const playerId = jugador.player_id;
            const jugadoresCount = grupo.jugadores.length;
            const jugadoresLabel = `${jugadoresCount} ${jugadoresCount === 1 ? 'jugador' : 'jugadores'} en este grupo`;
            // ESTADO A: Reconocido (Dashboard del Prode)
            // Mock de datos para el Dashboard según especificación del núcleo
            // Carrusel "Próximos partidos" — datos reales del Mundial leídos
            // de Supabase vía loadHomeMatches() (FASE 4 · Fixture conectado).
            // Antes acá vivía el mock `mockDailyMatches`. Se carga async tras
            // el render: el carrusel arranca con una card de "Cargando...".
            let dailyMatches = [];
            const loadingPartidosCard = `
                <div class="carousel-item" style="flex: 0 0 100%; box-sizing: border-box; scroll-snap-align: center; padding: 0 0.5rem;">
                    <div class="glass-card carousel-card" style="height: 100%; padding: 2rem 1.5rem; text-align: center; color: var(--text-muted); background: rgba(255,255,255,0.03);">
                        Cargando partidos del Mundial...
                    </div>
                </div>
            `;

            // H1 — Línea de contexto "GRUPO A · 11 JUN · 16:00" o "OCTAVOS · 29 JUN · 17:00".
            // Formateo en hora Argentina (la app es local de Argentina hasta 27-nov).
            const FASE_LABELS = { grupo: 'GRUPO', octavos: 'OCTAVOS', cuartos: 'CUARTOS', semifinal: 'SEMIFINAL', final: 'FINAL' };
            const MES_ABREV = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];
            const fmtDiaMes = (iso) => {
                const parts = new Intl.DateTimeFormat('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day:'numeric', month:'numeric' }).formatToParts(new Date(iso));
                const d = parts.find(p=>p.type==='day')?.value || '';
                const m = parts.find(p=>p.type==='month')?.value || '';
                return `${d} ${MES_ABREV[Number(m)-1] || ''}`;
            };
            const fmtHora = (iso) => new Intl.DateTimeFormat('es-AR', { timeZone:'America/Argentina/Buenos_Aires', hour:'2-digit', minute:'2-digit', hour12:false }).format(new Date(iso));
            const contextoLine = (match) => {
                const faseLabel = FASE_LABELS[match.fase] || (match.fase || '').toUpperCase();
                const sufijo = match.fase === 'grupo' && match.grupo_mundial ? ` ${match.grupo_mundial}` : '';
                return `${faseLabel}${sufijo} · ${fmtDiaMes(match.fecha_hora)} · ${fmtHora(match.fecha_hora)}`;
            };
            const statusBadge = (status) => {
                if (status === 'live') return `<span class="status-badge status-live">🔴 EN VIVO</span>`;
                if (status === 'finished') return `<span class="status-badge status-finished">FINALIZADO</span>`;
                return `<span class="status-badge status-scheduled">PROGRAMADO</span>`;
            };
            const pickLabel = (match, pick) => {
                if (pick === 'home') return `Gana ${match.home_team.name}`;
                if (pick === 'draw') return 'Empate';
                if (pick === 'away') return `Gana ${match.away_team.name}`;
                return '';
            };

            // H1 fix 2 — Accordion "Ver puntos en juego" para partidos de fase
            // de grupos (PROGRAMADO o EN VIVO). DT-DO: duplicado del Shadow DOM
            // de prode.js (líneas 132-134 + 175-178 + función renderScoringToggle).
            // Unificar post-piloto extrayendo a un componente compartido —
            // pendiente porque el CSS de Prode vive aislado en Shadow DOM y
            // requiere portarlo también.
            const openScoring = new Set();
            const SCORING_GRUPO = [
                ['Acierto ganador', '+5'],
                ['Empate acertado', '+1'],
                ['Error leve', '0'],
                ['Error contrario', '-1'],
            ];
            const renderScoringRow = (match) => {
                if (match.fase !== 'grupo') return '';
                if (match.result_status === 'finished') return '';
                const isOpen = openScoring.has(match.partido_id);
                const tabla = isOpen
                    ? `<div class="home-scoring-toggle"><div class="home-scoring-table">${SCORING_GRUPO.map(([l,v]) => `<span class="home-scoring-label">${l}</span><span class="home-scoring-value">${v}</span>`).join('')}</div></div>`
                    : '';
                return `
                    <div style="display:grid; grid-template-columns:1fr auto 1fr; align-items:center; margin-top:0.7rem;">
                        <span class="home-max-points">Hasta 5 pts</span>
                        <button class="home-score-toggle ${isOpen ? 'active' : ''}" type="button" data-score="${match.partido_id}">Ver puntos en juego</button>
                        <span></span>
                    </div>
                    ${tabla}
                `;
            };

            // H4 migración: shape "partido raw" para el componente compartido.
            const playoffPartidoFromMatch = (match) => ({
                partido_id: match.partido_id,
                fase: match.fase,
                fecha_hora: match.fecha_hora,
                status: match.result_status,
                equipo_local_codigo: match.equipo_local_codigo,
                equipo_visitante_codigo: match.equipo_visitante_codigo,
                equipo_local_nombre: match.home_team?.name || match.equipo_local_codigo,
                equipo_visitante_nombre: match.away_team?.name || match.equipo_visitante_codigo,
                // Banderas crudas: sin esto la card de playoff cae al fallback emoji
                // (muestra solo el código). aPartidoCarrusel las expone al tope.
                equipo_local_bandera_url: match.equipo_local_bandera_url,
                equipo_visitante_bandera_url: match.equipo_visitante_bandera_url,
                // Resultado + estado EN VIVO: sin estos campos la card de playoff no puede
                // mostrar el marcador real ni el subestado/minuto en vivo (igual que grupos).
                goles_local: match.goles_local,
                goles_visitante: match.goles_visitante,
                status_detail: match.status_detail,
                minuto: match.minuto,
            });

            const buildPartidoCards = (matches) => matches.map(match => {
                const status = match.result_status;
                const pick = match.user_prediction;

                // H4 migración: playoffs (cualquier estado) renderizan con el
                // componente compartido. Wrapper externo de carrusel envuelve
                // sin agregar borde ni padding (el componente trae el suyo).
                if (match.fase !== 'grupo') {
                    const playoffPartido = playoffPartidoFromMatch(match);
                    const predInicial = decodePrediccionPlayoff(match.user_prediction_raw, playoffPartido);
                    return `
                        <div class="carousel-item" style="flex: 0 0 100%; box-sizing: border-box; scroll-snap-align: center; padding: 0 0.5rem;">
                            <div class="glass-card carousel-card" style="height: 100%; padding: 0; background: transparent; border: none; box-shadow: none; transition: transform 0.3s ease, opacity 0.3s ease; opacity: 0.6; transform: scale(0.95);">
                                ${renderPlayoffCardHome(playoffPartido, predInicial, openScoring.has(playoffPartido.partido_id))}
                            </div>
                        </div>
                    `;
                }

                let botonesOResultado = '';
                if (status === 'scheduled') {
                    // DT-37/38: si el kickoff ya pasó (partido en curso, pero el reloj
                    // todavía no lo marcó 'en vivo'), NO mostramos botones de predicción:
                    // el partido ya arrancó y la jugada está cerrada.
                    const yaArranco = new Date(match.fecha_hora).getTime() <= Date.now();
                    const btn = (val, label) => {
                        const sel = pick === val;
                        const bg = sel ? 'var(--celeste)' : 'rgba(255,255,255,0.08)';
                        const col = sel ? '#000' : 'var(--white)';
                        const fw = sel ? '700' : '500';
                        return `<button class="btn btn-secondary pred-btn" data-match="${match.partido_id}" data-pred="${val}" style="flex:1; padding:0.8rem 0.2rem; font-size:0.75rem; margin:0; background:${bg}; color:${col}; font-weight:${fw};">${label}</button>`;
                    };
                    botonesOResultado = yaArranco ? '' : `
                        <div style="display:flex; gap:0.3rem; margin-top:1rem;">
                            ${btn('home', `${match.home_team.flag} Gana ${match.home_team.name}`)}
                            ${btn('draw', 'Empate')}
                            ${btn('away', `${match.away_team.flag} Gana ${match.away_team.name}`)}
                        </div>
                    `;
                } else if (status === 'live') {
                    const parcial = match.result ? `<div style="text-align:center; font-size:1.1rem; color:var(--white); margin-top:0.5rem;">${match.result.home} - ${match.result.away}</div>` : '';
                    const pickInfo = pick ? `<div style="text-align:center; color:var(--celeste); font-weight:bold; font-size:0.85rem; margin-top:0.3rem;">Elegiste: ${pickLabel(match, pick)}</div>` : '';
                    botonesOResultado = `
                        ${parcial}${pickInfo}
                        <div style="display:flex; gap:0.3rem; margin-top:1rem; opacity:0.4; pointer-events:none; cursor:not-allowed;">
                            <button class="btn btn-secondary" disabled style="flex:1; padding:0.8rem 0.2rem; font-size:0.75rem; margin:0; background:rgba(255,255,255,0.08);">${match.home_team.flag} Gana ${match.home_team.name}</button>
                            <button class="btn btn-secondary" disabled style="flex:1; padding:0.8rem 0.2rem; font-size:0.75rem; margin:0; background:rgba(255,255,255,0.08);">Empate</button>
                            <button class="btn btn-secondary" disabled style="flex:1; padding:0.8rem 0.2rem; font-size:0.75rem; margin:0; background:rgba(255,255,255,0.08);">${match.away_team.flag} Gana ${match.away_team.name}</button>
                        </div>
                    `;
                } else if (status === 'finished') {
                    // DT-38: el marcador ya va centrado entre las banderas; abajo dejamos
                    // solo el detalle de la jugada (sin repetir "Resultado: X-Y").
                    if (!pick) {
                        botonesOResultado = `
                            <div style="text-align:center; margin-top:1rem;">
                                <div style="color:var(--text-muted); font-size:0.9rem;">No jugaste · 0 pts</div>
                            </div>
                        `;
                    } else {
                        const pts = match.points_awarded ?? 0;
                        const ptsColor = pts > 0 ? '#4caf50' : pts < 0 ? '#ff6b6b' : 'var(--text-muted)';
                        const ptsText = pts > 0 ? `+${formatNumber(pts)} pts` : `${formatNumber(pts)} pts`;
                        botonesOResultado = `
                            <div style="text-align:center; margin-top:1rem;">
                                <div style="font-size:0.85rem; color:var(--celeste); margin-bottom:0.2rem;">Elegiste: ${pickLabel(match, pick)}</div>
                                <div style="color:${ptsColor}; font-weight:bold; font-size:1rem;">${ptsText}</div>
                            </div>
                        `;
                    }
                }

                return `
                    <div class="carousel-item" style="flex: 0 0 100%; box-sizing: border-box; scroll-snap-align: center; padding: 0 0.5rem;">
                        <div class="glass-card carousel-card" style="position:relative; height: 100%; padding: 2rem 1.5rem; border: 1px solid rgba(255,255,255,0.18); display: flex; flex-direction: column; justify-content: center; background: rgba(255, 255, 255, 0.03); transition: transform 0.3s ease, opacity 0.3s ease; opacity: 0.6; transform: scale(0.95);">
                            <div style="position:absolute; top:0.6rem; right:0.7rem; display:flex; flex-direction:column; align-items:flex-end; gap:0.15rem;">
                                ${statusBadge(status)}
                                ${(() => {
                                    // Subestado + minuto del partido en vivo, apilado bajo el badge,
                                    // con el mismo helper/lógica que PRODE.
                                    if (status !== 'live') return '';
                                    const { subestado, minuto } = lineasEstadoVivo(match);
                                    return `
                                        <span style="font-size:0.6rem; font-weight:700; color:var(--text-muted); text-transform:none;">${subestado}</span>
                                        ${minuto ? `<span style="font-size:0.6rem; font-weight:700; color:var(--text-muted);">${minuto}</span>` : ''}
                                    `;
                                })()}
                            </div>
                            <div style="text-align:center; font-size:0.7rem; color:var(--text-muted); letter-spacing:1px; font-weight:700; margin-bottom:0.7rem; margin-top:0.5rem;">${contextoLine(match)}</div>
                            <div style="text-align: center; margin-bottom: 0.5rem; display: flex; justify-content: center; align-items: center; gap: 1rem;">
                                <div style="display: flex; flex-direction: column; align-items: center; gap: 0.3rem;">
                                    <span style="font-size: 2rem;">${match.home_team.flag}</span>
                                    <span style="font-size: 1.2rem; font-family: var(--font-heading); color: var(--white);">${match.home_team.code}</span>
                                </div>
                                ${(() => {
                                    // DT-38: en el centro, marcador en vivo/final (mismo formato que PRODE);
                                    // si no hay goles (programado), seguimos mostrando "vs".
                                    const marcador = resultadoCentroHome(match);
                                    return marcador
                                        ? `<span style="color: var(--white); font-family: var(--font-heading); font-size: 1.5rem; font-weight: 800; margin: 0 0.5rem; white-space: nowrap;">${marcador}</span>`
                                        : `<span style="color: var(--text-muted); font-size: 1rem; margin: 0 0.5rem;">vs</span>`;
                                })()}
                                <div style="display: flex; flex-direction: column; align-items: center; gap: 0.3rem;">
                                    <span style="font-size: 2rem;">${match.away_team.flag}</span>
                                    <span style="font-size: 1.2rem; font-family: var(--font-heading); color: var(--white);">${match.away_team.code}</span>
                                </div>
                            </div>
                            ${botonesOResultado}
                            ${renderScoringRow(match)}
                        </div>
                    </div>
                `;
            }).join('');

            // H4 migración: tras cada repaint del carrusel, wirear las cards
            // de playoff con el callback que persiste vía RPC guardar_prediccion.
            const wireCarouselPlayoffCards = () => {
                const cont = document.getElementById('carousel-partidos');
                if (!cont) return;
                cont.querySelectorAll('[data-ppc-home]').forEach((el) => {
                    const id = el.getAttribute('data-ppc-home');
                    const match = dailyMatches.find((m) => m.partido_id === id);
                    if (!match) return;
                    const playoffPartido = playoffPartidoFromMatch(match);
                    const predInicial = decodePrediccionPlayoff(match.user_prediction_raw, playoffPartido);
                    const wrap = el.closest('.ppc-wrap');
                    if (!wrap) return;
                    wirePlayoffCard(wrap, playoffPartido, predInicial, {
                        onSave: async (payload) => {
                            const { error } = await upsertPrediccion(playerId, payload.partido_id, payload);
                            if (error) {
                                console.error('[grupo] upsertPrediccion playoff:', error);
                                return { ok: false, message: 'No pudimos guardar. Probá de nuevo.' };
                            }
                            match.user_prediction_raw = {
                                prediction_type: 'playoff_match',
                                score_local: payload.score_local,
                                score_visitante: payload.score_visitante,
                                clasificado_codigo: payload.clasificado_codigo,
                            };
                            return { ok: true };
                        },
                    });
                });
            };

            let cardsHTML = dailyMatches.length ? buildPartidoCards(dailyMatches) : loadingPartidosCard;

            // F1.4.b.2 · DT-CO/DT-CP: alineación con spec §2 del doc Pacto v1.1.
            // El estado canónico en DB es 'certificado' (no 'activo' como decía
            // el comment STALE de core/pacto.js). 4 ramas distintas según
            // estado + (si firmar) mayoría. La posición del bloque (arriba si
            // no-certificado, al final si certificado) la decide isPactoActivo
            // en el template — preservado.
            const isPactoActivo = grupo.pacto && grupo.pacto.estado === 'certificado';
            const total = grupo.jugadores.length;
            const umbral = Math.ceil(total / 2);
            let pactoButtonHTML = '';

            if (!grupo.pacto || grupo.pacto.estado === 'definir') {
                // Rama 1: definir. Amarillo, botón "Definir".
                pactoButtonHTML = `
                    <div class="glass-card mb-4" style="padding: 1rem; border-color: rgba(255, 215, 0, 0.3); background: rgba(255,215,0,0.05); display: flex; justify-content: space-between; align-items: center;">
                        <span style="color: #FFD700; font-size: 0.9rem; font-weight: bold;">📜 Pacto sin definir</span>
                        <button class="btn btn-primary" style="margin:0; padding: 0.4rem 0.8rem; font-size: 0.8rem;" onclick="window.location.hash='/pacto/${codigo}'">Definir</button>
                    </div>
                `;
            } else if (grupo.pacto.estado === 'certificado') {
                // Rama 4: certificado. Verde, botón "Ver pacto".
                pactoButtonHTML = `
                    <div class="glass-card mb-4" style="padding: 1rem; border-color: rgba(76, 175, 80, 0.28); background: rgba(76,175,80,0.06); display: flex; justify-content: space-between; align-items: center; gap: 1rem;">
                        <div>
                            <span style="color: #4caf50; font-size: 0.9rem; font-weight: bold; display: block;">✅ Pacto certificado</span>
                            <span style="font-size: 0.75rem; color: var(--text-muted);">Vigente para este grupo</span>
                        </div>
                        <button class="btn btn-secondary" style="margin:0; padding: 0.4rem 0.8rem; font-size: 0.8rem; width: auto; color: var(--celeste);" onclick="window.location.hash='/pacto/${codigo}'">Ver pacto</button>
                    </div>
                `;
            } else {
                // Rama 2 o 3: estado 'firmar'. La mayoría decide si el botón
                // es "Firmar pacto" (celeste) o "Certificar pacto" (verde).
                // Decisión amarilla F1.4.b.2: mostrar X/Y firmaron en ambos
                // casos para que el usuario vea el progreso antes de tocar.
                const firmas = grupo.pacto.firmas || [];
                const hayMayoria = firmas.length >= umbral;
                if (hayMayoria) {
                    pactoButtonHTML = `
                        <div class="glass-card mb-4" style="padding: 1rem; border-color: rgba(76, 175, 80, 0.32); background: rgba(76,175,80,0.07); display: flex; justify-content: space-between; align-items: center;">
                            <span style="color: #4caf50; font-size: 0.9rem; font-weight: bold;">📜 Pacto · ${firmas.length}/${total} firmaron</span>
                            <button class="btn btn-primary" style="margin:0; padding: 0.4rem 0.8rem; font-size: 0.8rem; background: #4caf50;" onclick="window.location.hash='/pacto/${codigo}'">Certificar pacto</button>
                        </div>
                    `;
                } else {
                    pactoButtonHTML = `
                        <div class="glass-card mb-4" style="padding: 1rem; border-color: rgba(117, 170, 219, 0.32); background: rgba(117,170,219,0.07); display: flex; justify-content: space-between; align-items: center;">
                            <span style="color: var(--celeste); font-size: 0.9rem; font-weight: bold;">📜 Pacto · ${firmas.length}/${total} firmaron</span>
                            <button class="btn btn-primary" style="margin:0; padding: 0.4rem 0.8rem; font-size: 0.8rem;" onclick="window.location.hash='/pacto/${codigo}'">Firmar pacto</button>
                        </div>
                    `;
                }
            }

            const ritualesHTML = `
                <div class="mb-4">
                    <button class="btn btn-primary" style="margin:0; padding: 1rem; min-height: 68px; font-size: 1rem; width: 100%; font-family: var(--font-heading); text-transform: uppercase; letter-spacing: 0.5px; background: linear-gradient(135deg, #75aadb 0%, #a855f7 58%, #f59e0b 100%); border: none; box-shadow: 0 6px 18px rgba(168,85,247,0.28); display: flex; align-items: center; justify-content: space-between; gap: 1rem; text-align: left;" onclick="window.location.hash='/rituales/${codigo}/arengas'">
                        <span style="display: flex; flex-direction: column; gap: 0.18rem; min-width: 0;">
                            <span style="font-size: 1rem;">⚽ Rituales del grupo</span>
                            <span style="font-family: var(--font-body); text-transform: none; letter-spacing: 0; font-size: 0.78rem; line-height: 1.2; color: rgba(255,255,255,0.82); font-weight: 600;">3 cábalas · 4 promesas · 5 arengas</span>
                        </span>
                        <span style="font-size: 1.55rem; flex: 0 0 auto;">🔥</span>
                    </button>
                </div>
            `;

            appRoot.innerHTML = `
                <div class="view-enter" style="width: 100%; animation: fadeIn 0.4s ease-out forwards;">
                    ${renderActiveGroupBar(codigo)}
                    
                    ${!isPactoActivo ? pactoButtonHTML : ''}

                    ${buildComoSeJuegaButton()}

                    <div class="mb-4">
                        <div style="margin-bottom: 0.5rem;">
                            <h3 style="font-size: 1.1rem; color: var(--white); display: flex; align-items: center; gap: 0.5rem;">
                                <span>🔥</span> Próximos partidos
                            </h3>
                        </div>
                        <style>
                            @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
                            .status-badge { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.5px; padding: 0.18rem 0.45rem; border-radius: 3px; text-transform: uppercase; }
                            .status-scheduled { background: rgba(117,170,219,0.18); color: var(--celeste); border: 1px solid rgba(117,170,219,0.3); }
                            .status-live { background: rgba(255,107,107,0.18); color: #ff6b6b; border: 1px solid rgba(255,107,107,0.4); animation: pulse 1.5s infinite; }
                            .status-finished { background: rgba(255,255,255,0.05); color: var(--text-muted); border: 1px solid rgba(255,255,255,0.1); }
                            /* H1 fix 2 · DT-DO: accordion "Ver puntos en juego" — duplicado literal del Shadow DOM de prode.js (líneas 132-134 + 175-178). Clases prefijadas con home- para evitar colisión teórica si en el futuro alguno se monta fuera de Shadow DOM. */
                            .home-max-points { color: rgba(187,247,208,.7); font-size: 9.5px; font-weight: 400; white-space: nowrap; justify-self: start; }
                            .home-score-toggle { justify-self: center; min-height: 23px; padding: 0 8px; border: 1px solid rgba(255,255,255,.075); border-radius: 999px; color: rgba(226,232,240,.82); background: rgba(255,255,255,.025); font-size: 10px; font-weight: 800; cursor: pointer; }
                            .home-score-toggle.active { border-color: rgba(117,170,219,.18); background: rgba(117,170,219,.06); color: rgba(226,232,240,.94); }
                            .home-scoring-toggle { width: fit-content; min-width: 220px; margin: 0.4rem auto 0; border: 1px solid rgba(117,170,219,.16); border-radius: 9px; background: rgba(117,170,219,.045); }
                            .home-scoring-table { display: grid; grid-template-columns: max-content 34px; column-gap: 10px; row-gap: 4px; justify-content: center; padding: 8px 12px; color: rgba(148,163,184,.82); font-size: 11px; }
                            .home-scoring-label { text-align: right; }
                            .home-scoring-value { color: rgba(248,250,252,.94); text-align: right; font-weight: 900; }
                            .carousel-container { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; gap: 0; scroll-behavior: smooth; -ms-overflow-style: none; scrollbar-width: none; margin-left: -0.5rem; margin-right: -0.5rem; }
                            .carousel-container::-webkit-scrollbar { display: none; }
                            .carousel-arrow { position: absolute; top: 50%; transform: translateY(-50%); background: transparent; color: var(--white); border: none; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 10; opacity: 0.5; font-size: 1.6rem; transition: all 0.3s ease; font-weight: 300; }
                            .carousel-arrow:hover { opacity: 1; transform: translateY(-50%) scale(1.1); }
                            .carousel-arrow.left { left: 0px; }
                            .carousel-arrow.right { right: 0px; }
                        </style>
                        <div style="position: relative;">
                            <button class="carousel-arrow left" onclick="document.getElementById('carousel-partidos').scrollBy({left: -300, behavior: 'smooth'})">‹</button>
                            <div class="carousel-container" id="carousel-partidos">
                                ${cardsHTML}
                            </div>
                            <button class="carousel-arrow right" onclick="document.getElementById('carousel-partidos').scrollBy({left: 300, behavior: 'smooth'})">›</button>
                        </div>
                        <div style="display: flex; justify-content: center; gap: 0.4rem; margin-top: 0.8rem; margin-bottom: 1rem;" id="dots-partidos">
                            ${dailyMatches.map((_, i) => `<div style="width: 6px; height: 6px; border-radius: 50%; background: ${i === 0 ? 'var(--celeste)' : 'rgba(255,255,255,0.3)'}; transition: all 0.3s ease; transform: ${i === 0 ? 'scale(1.3)' : 'scale(1)'};"></div>`).join('')}
                        </div>
                    </div>

                    ${ritualesHTML}
                    
                    ${isPactoActivo ? pactoButtonHTML : ''}

                    <button class="btn btn-secondary" id="btnLogoutGrupo" data-action="logout-grupo" style="font-size:0.8rem; opacity:0.7; margin-top:0.6rem;">Cerrar sesión</button>
                </div>
                ${renderBottomNav(codigo, 'grupo')}
            `;

            // Onboarding "Cómo se juega": se auto-abre la primera vez que el
            // usuario entra a un grupo en este device (flag pm_csj_visto). El
            // botón 🏆 ¿Cómo se juega? (arriba) lo reabre cuando quiera.
            maybeAutoOpenComoSeJuega();

            // H1 — Predicciones: listener delegado en el contenedor del
            // carrusel, sobrevive al re-render async de las cards. Persistencia
            // unificada con Prode vía el RPC guardar_prediccion (mismo path
            // que prode.js persistPrediction). Playoffs requieren goles +
            // clasificado: el home redirige a Prode para esos partidos.
            document.getElementById('carousel-partidos')?.addEventListener('click', async (e) => {
                // Toggle del accordion "Ver puntos en juego" (H1 fix 2).
                const scoreBtn = e.target.closest('.home-score-toggle');
                if (scoreBtn) {
                    e.preventDefault();
                    const id = scoreBtn.getAttribute('data-score');
                    if (openScoring.has(id)) openScoring.delete(id);
                    else openScoring.add(id);
                    const cont = document.getElementById('carousel-partidos');
                    if (cont) cont.innerHTML = buildPartidoCards(dailyMatches);
                    wireCarouselPlayoffCards();
                    return;
                }
                const btn = e.target.closest('.pred-btn');
                if (!btn || btn.disabled) return;
                const partidoId = btn.getAttribute('data-match');
                const pred = btn.getAttribute('data-pred');
                if (!partidoId || !pred || !playerId) return;
                const match = dailyMatches.find(m => m.partido_id === partidoId);
                if (match && match.fase !== 'grupo') {
                    window.location.hash = `/prode/${codigo}`;
                    return;
                }
                const [scoreLocal, scoreVisitante] = pred === 'home' ? [1, 0] : pred === 'away' ? [0, 1] : [0, 0];
                btn.disabled = true;
                const { error } = await upsertPrediccion(playerId, partidoId, {
                    prediction_type: 'group_match',
                    score_local: scoreLocal,
                    score_visitante: scoreVisitante,
                    clasificado_codigo: null,
                });
                if (error) {
                    btn.disabled = false;
                    alert('No pudimos guardar la predicción. Probá de nuevo.');
                    console.error('[grupo] upsertPrediccion:', error);
                    return;
                }
                if (match) match.user_prediction = pred;
                openScoring.delete(partidoId);
                const cont = document.getElementById('carousel-partidos');
                if (cont) cont.innerHTML = buildPartidoCards(dailyMatches);
                wireCarouselPlayoffCards();
            });

            // DT-K + fix 31-may (segunda iteración, dueño confirmó que el
            // intento previo dejaba al usuario en "Sumate a Los bandidos"):
            // limpieza COMPLETA de storage (no solo sesion_<codigo>) +
            // window.location.href = '/' (hard reload) en vez de hash
            // change. El hash change carrera con onAuthStateChange: el
            // callback de auth corre renderApp() ANTES de mi línea, con
            // hash todavía /g/<codigo> + authed=false → handleGuestRoute
            // pinta renderInvitadoSinSesion y el siguiente render queda
            // tapado. El reload total evita toda carrera y entra limpio
            // a renderLanding desde bootstrap.
            //
            // Keys identificadas que disparan handleGuestRoute / re-entry
            // al grupo si no se limpian:
            //   localStorage: sesion_<codigo>, grupo_activo
            //   sessionStorage: pacto:loginIntent, pacto:joining
            const _logoutGrupoHandler = async (e) => {
                const btn = e.target.closest('[data-action="logout-grupo"]');
                if (!btn) return;
                e.preventDefault();
                // Tercera iteración (31-may): el dueño confirmó parpadeo
                // "El grupo no existe" antes del reload. Causa: post-signOut,
                // onAuthStateChange dispara renderApp con hash todavía
                // /g/<codigo> + authed=false → handleGuestRoute pinta
                // "Cargando grupo..." → getGroupPreview falla (sin sesión RLS)
                // → renderGrupoInexistente "El grupo no existe" → reload.
                // Fix: limpiar hash ANTES de signOut con history.replaceState
                // (no dispara hashchange ni render intermedio). Cuando
                // SIGNED_OUT corra renderApp, el hash ya es '/' → cae a
                // renderLanding directo. location.replace al final evita
                // entry al back-button.
                try { window.history.replaceState(null, '', '/'); } catch (_) { /* ignore */ }
                localStorage.removeItem(`sesion_${codigo}`);
                localStorage.removeItem('grupo_activo');
                try {
                    sessionStorage.removeItem('pacto:loginIntent');
                    sessionStorage.removeItem('pacto:joining');
                } catch (_) { /* ignore */ }
                await signOut();
                // Full reload (audit 5-jun): location.replace('/') es NO-OP si ya
                // estamos en '/' (acá el replaceState previo dejó la URL en '/') →
                // el contexto JS persistía y el cliente Supabase quedaba zombie,
                // colgando el próximo login. '/?_=<ts>' fuerza navegación real →
                // contexto JS fresco → cliente Supabase limpio.
                window.location.href = '/?_=' + Date.now();
            };
            appRoot.removeEventListener('click', appRoot._logoutGrupoHandler || (() => {}));
            appRoot._logoutGrupoHandler = _logoutGrupoHandler;
            appRoot.addEventListener('click', _logoutGrupoHandler);

            const initCarousel = (carouselId, dotsId) => {
                const container = document.getElementById(carouselId);
                const dotsContainer = document.getElementById(dotsId);
                if(!container || !dotsContainer) return;
                const dots = dotsContainer.children;
                const cards = container.querySelectorAll('.carousel-card');
                const items = container.querySelectorAll('.carousel-item');
                
                const leftArrow = container.parentElement.querySelector('.carousel-arrow.left');
                const rightArrow = container.parentElement.querySelector('.carousel-arrow.right');
                
                const updateArrows = () => {
                    if(leftArrow) {
                        leftArrow.style.opacity = container.scrollLeft <= 5 ? '0' : '0.5';
                        leftArrow.style.pointerEvents = container.scrollLeft <= 5 ? 'none' : 'auto';
                    }
                    const maxScroll = container.scrollWidth - container.clientWidth;
                    if(rightArrow) {
                        rightArrow.style.opacity = container.scrollLeft >= maxScroll - 5 ? '0' : '0.5';
                        rightArrow.style.pointerEvents = container.scrollLeft >= maxScroll - 5 ? 'none' : 'auto';
                    }
                };

                if (cards.length > 0) {
                    cards[0].style.opacity = '1';
                    cards[0].style.transform = 'scale(1)';
                }

                // Initial arrow state
                setTimeout(updateArrows, 100);

                container.addEventListener('scroll', () => {
                    updateArrows();

                    const containerCenter = container.scrollLeft + container.clientWidth / 2;
                    let minDistance = Infinity;
                    let activeIndex = 0;
                    
                    items.forEach((item, i) => {
                        const itemCenter = item.offsetLeft + item.offsetWidth / 2 - container.offsetLeft;
                        const distance = Math.abs(itemCenter - containerCenter);
                        if(distance < minDistance) {
                            minDistance = distance;
                            activeIndex = i;
                        }
                    });
                    
                    Array.from(dots).forEach((dot, i) => {
                        if (i === activeIndex) {
                            dot.style.background = 'var(--celeste)';
                            dot.style.transform = 'scale(1.3)';
                        } else {
                            dot.style.background = 'rgba(255,255,255,0.3)';
                            dot.style.transform = 'scale(1)';
                        }
                    });

                    cards.forEach((card, i) => {
                        if (i === activeIndex) {
                            card.style.opacity = '1';
                            card.style.transform = 'scale(1)';
                        } else {
                            card.style.opacity = '0.6';
                            card.style.transform = 'scale(0.95)';
                        }
                    });
                });
            };

            // Carga async de los partidos reales del Mundial. El contenedor
            // #carousel-partidos persiste; al resolver se inyectan las cards y
            // recién ahí se inicializa su carrusel (una sola vez).
            // DT-H3-resto: tope a la carga de partidos; en timeout cae al .catch
            // de abajo ("No pudimos cargar los partidos"), sin colgar la sección.
            withTimeout(loadHomeMatches(playerId), NET_TIMEOUT_MS, 'home-matches').then(matches => {
                const cont = document.getElementById('carousel-partidos');
                const dotsBox = document.getElementById('dots-partidos');
                if (!cont || !dotsBox) return;
                dailyMatches = matches;
                cont.innerHTML = matches.length
                    ? buildPartidoCards(matches)
                    : `<div class="carousel-item" style="flex: 0 0 100%; box-sizing: border-box; padding: 0 0.5rem;"><div class="glass-card carousel-card" style="height: 100%; padding: 2rem 1.5rem; text-align: center; color: var(--text-muted); background: rgba(255,255,255,0.03);">No hay partidos próximos.</div></div>`;
                dotsBox.innerHTML = matches.map((_, i) => `<div style="width: 6px; height: 6px; border-radius: 50%; background: ${i === 0 ? 'var(--celeste)' : 'rgba(255,255,255,0.3)'}; transition: all 0.3s ease; transform: ${i === 0 ? 'scale(1.3)' : 'scale(1)'};"></div>`).join('');
                initCarousel('carousel-partidos', 'dots-partidos');
                wireCarouselPlayoffCards();
            }).catch(err => {
                console.error('[grupo] no se pudieron cargar los partidos del Mundial:', err);
                const cont = document.getElementById('carousel-partidos');
                if (cont) cont.innerHTML = `<div class="carousel-item" style="flex: 0 0 100%; box-sizing: border-box; padding: 0 0.5rem;"><div class="glass-card carousel-card" style="height: 100%; padding: 2rem 1.5rem; text-align: center; color: #ff9d9d; background: rgba(255,255,255,0.03);">No pudimos cargar los partidos.</div></div>`;
            });

            // F1.4.b · DT-AJ: hidratación async + realtime del pacto. El home
            // antes mostraba siempre "Pacto sin definir" porque grupo.pacto no
            // se hidrataba. Ahora: 1) si grupo.pacto está undefined, fetcheamos
            // y re-renderizamos cuando llegue; 2) suscripción realtime a la
            // tabla pacto filtrada por group_id → si otro miembro firma o
            // certifica, la card se actualiza en vivo.
            const hidratarPacto = async () => {
                try {
                    const p = await getPactoByGroup(grupo.group_id);
                    const previo = grupo.pacto;
                    // null = "no hay pacto" (estado válido). Comparación shallow:
                    // re-renderizamos solo si cambió algo material.
                    if (previo === p) return;
                    if (previo && p && previo.estado === p.estado && previo.version === p.version
                        && (previo.firmas?.length || 0) === (p.firmas?.length || 0)) return;
                    grupo.pacto = p;
                    // Re-render solo si seguimos en el home del grupo.
                    if (window.location.hash.startsWith(`#/g/${codigo}`)) renderGrupo(codigo);
                } catch (err) { console.error('[grupo] hidratarPacto:', err); }
            };
            if (grupo.pacto === undefined) hidratarPacto();

            limpiarSuscripciones('grupo');
            subscribirCambios('grupo', 'pacto', {
                event: '*',
                filter: `group_id=eq.${grupo.group_id}`,
            }, () => {
                // Force re-fetch en el próximo render.
                grupo.pacto = undefined;
                hidratarPacto();
            });

            return;
        }
    // No soy miembro del grupo (no está en el cache) → unirse va por /ingreso
    // (RPC unirse_a_grupo). Las ramas !currentUser / ESTADO B / ESTADO C
    // murieron en F1.3.b (DT-I / DT-X).
    navigate('/ingreso');
};

window.renderGrupo = renderGrupo;
